/**
 * QuestionOptionsList Component
 *
 * Displays a list of question options with letter labels (A, B, C, etc.)
 * Used in question cards for multiple choice questions. When an option carries a
 * diagnostic tag (Diagnostic Feedback System), it is shown as a small badge —
 * read-only here; interactive tagging lives in the Diagnostic Tag Review page.
 *
 * @module QuestionOptionsList
 */

export type { OptionObject, OptionValue } from '@/lib/questionOptions';
export { optionText } from '@/lib/questionOptions';
import { optionText, type OptionValue } from '@/lib/questionOptions';
import OptionTagBadges from '@/components/admin/OptionTagBadges';

interface QuestionOptionsListProps {
  readonly options: readonly OptionValue[];
  readonly className?: string;
}

/**
 * Component that renders a list of question options with letter labels.
 *
 * @param options - Array of option strings/objects to display
 * @param className - Optional additional CSS classes
 */
export default function QuestionOptionsList({
  options,
  className = "",
}: QuestionOptionsListProps) {
  if (!options || options.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <h6 className="text-xs font-medium text-muted-foreground mb-2 font-ui">
        Options
      </h6>
      <div className="grid grid-cols-1 gap-1">
        {options.map((option: OptionValue, optionIndex: number) => (
            <div
              key={`option-${optionIndex}`}
              className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground pl-2 font-ui"
            >
              <span>
                {String.fromCodePoint(65 + optionIndex)}. {optionText(option)}
              </span>
              <OptionTagBadges option={option} />
            </div>
        ))}
      </div>
    </div>
  );
}
